import { supabase } from '../lib/supabase';
export class SupabaseWorkspaceService {
    /**
     * Load all workspaces from Supabase, optionally filtered by organization
     */
    async loadWorkspaces(organizationId) {
        try {
            console.log('🔍 Loading workspaces from Supabase...', organizationId ? `for org ${organizationId}` : '');
            // Get current user
            const { data: { user }, error: authError } = await supabase.auth.getUser();
            if (authError || !user) {
                console.error('Error getting current user:', authError);
                return [];
            }
            // Fetch workspaces the user is a member of (from workspace_members table)
            const { data: membershipData, error: membershipError } = await supabase
                .from('workspace_members')
                .select('workspace_id, workspaces(*)')
                .eq('user_id', user.id);
            if (membershipError) {
                console.error('Error loading workspace memberships:', membershipError);
                return [];
            }
            // Extract workspace data from membership records
            const workspacesData = (membershipData || [])
                .map((membership) => membership.workspaces)
                .filter((workspace) => {
                // Optionally filter by organization if provided
                if (organizationId) {
                    return workspace.organization_id === organizationId;
                }
                return true;
            });
            console.log('✅ Loaded user workspaces:', workspacesData);
            // Fetch boards ONLY for the loaded workspaces
            const workspaceIds = workspacesData.map(ws => ws.id);
            let boardsData = [];
            if (workspaceIds.length > 0) {
                const { data, error: boardsError } = await supabase
                    .from('boards')
                    .select('*')
                    .in('workspace_id', workspaceIds)
                    .order('created_at', { ascending: true });
                if (boardsError) {
                    console.error('Error loading boards:', boardsError);
                }
                else {
                    boardsData = data || [];
                }
            }
            console.log('✅ Loaded boards:', boardsData);
            // Transform Supabase data to workspace format
            // Prioritize workspaces with emoji or "MyCow Group" name (our migrated data)
            const sortedWorkspaces = workspacesData.sort((a, b) => {
                if (a.name.includes('🐮') && !b.name.includes('🐮'))
                    return -1;
                if (!a.name.includes('🐮') && b.name.includes('🐮'))
                    return 1;
                return a.created_at.localeCompare(b.created_at);
            });
            console.log('📊 Workspace priority order:', sortedWorkspaces.map(w => w.name));
            // Fetch all workspace items in parallel for ALL loaded workspaces
            const [foldersData, dashboardsData, docsData, formsData, appsData] = await Promise.all([
                this.loadAllFolders(workspaceIds),
                this.loadAllDashboards(workspaceIds),
                this.loadAllDocs(workspaceIds),
                this.loadAllForms(workspaceIds),
                this.loadAllWorkspaceApps(workspaceIds)
            ]);
            console.log('✅ Loaded folders for all workspaces:', foldersData);
            console.log('✅ Loaded dashboards for all workspaces:', dashboardsData);
            console.log('✅ Loaded docs for all workspaces:', docsData);
            console.log('✅ Loaded forms for all workspaces:', formsData);
            console.log('✅ Loaded apps for all workspaces:', appsData);
            const workspaces = sortedWorkspaces.map(ws => {
                // Find boards for this workspace
                const workspaceBoards = boardsData?.filter(board => board.workspace_id === ws.id) || [];
                // Separate boards into categories:
                // 1. User-created boards (no monday_board_id) -> always display as boards
                // 2. Migrated strategic boards (monday_board_id + "Q1 Strategic" in title) -> display as boards
                // 3. Migrated department boards (monday_board_id + department prefix) -> convert to folders
                const userCreatedBoards = workspaceBoards.filter(board => !board.monday_board_id);
                const migratedStrategicBoards = workspaceBoards.filter(board => board.monday_board_id && (board.title.includes('Q1 Strategic') || board.monday_board_id === '8198396724'));
                const migratedDepartmentBoards = workspaceBoards.filter(board => board.monday_board_id &&
                    !board.title.includes('Q1 Strategic') &&
                    board.monday_board_id !== '8198396724');
                // Create folders for migrated department boards only
                const folders = migratedDepartmentBoards.map(board => ({
                    id: `folder-${board.id}`,
                    name: board.title.replace(' - ', ' '),
                    color: this.getBoardColor(board.title),
                    workspaceId: ws.id,
                    collapsed: false,
                    boards: [], // Department folders will contain sub-boards when created
                    dashboards: [],
                    docs: [],
                    forms: [],
                    apps: [],
                    subFolders: [],
                    createdAt: new Date(board.created_at),
                    updatedAt: new Date(board.updated_at)
                }));
                // Debug: Show which boards are being converted to folders
                if (migratedDepartmentBoards.length > 0) {
                    console.log(`🔄 Converting ${migratedDepartmentBoards.length} migrated department boards to folders:`, migratedDepartmentBoards.map(b => b.title));
                }
                // Convert all non-department boards to workspace boards (user-created + strategic)
                // IMPORTANT: We do NOT include migratedDepartmentBoards here because they are now folders
                const allDisplayBoards = [...userCreatedBoards, ...migratedStrategicBoards];
                const boards = allDisplayBoards.map(board => ({
                    id: board.id,
                    name: board.title,
                    description: board.description || '',
                    workspaceId: ws.id,
                    folderId: board.folder_id || undefined, // Read folder_id from Supabase
                    ownerId: board.created_by,
                    color: this.getBoardColor(board.title),
                    starred: board.is_starred || false,
                    boardType: 'flexiboard',
                    createdAt: new Date(board.created_at),
                    updatedAt: new Date(board.updated_at),
                    mondayBoardId: board.monday_board_id
                }));
                // Load user-created folders from Supabase (not migrated department folders)
                const userFolders = foldersData.filter((f) => f.workspace_id === ws.id).map((f) => ({
                    id: f.id,
                    name: f.name,
                    description: f.description || '',
                    color: f.color || '#579bfc',
                    workspaceId: ws.id,
                    parentId: f.parent_folder_id,
                    collapsed: f.collapsed || false,
                    boards: [],
                    dashboards: [],
                    docs: [],
                    forms: [],
                    apps: [],
                    subFolders: [],
                    createdAt: new Date(f.created_at),
                    updatedAt: new Date(f.updated_at)
                }));
                // Combine migrated department folders with user-created folders
                const allFolders = [...folders, ...userFolders];
                // Transform dashboards from Supabase
                const dashboards = dashboardsData.filter((d) => d.workspace_id === ws.id).map((d) => ({
                    id: d.id,
                    name: d.name,
                    description: d.description || '',
                    workspaceId: ws.id,
                    folderId: d.folder_id,
                    layout: d.layout || { widgets: [] },
                    createdAt: new Date(d.created_at),
                    updatedAt: new Date(d.updated_at)
                }));
                // Transform docs from Supabase
                const docs = docsData.filter((doc) => doc.workspace_id === ws.id).map((doc) => ({
                    id: doc.id,
                    name: doc.name,
                    content: doc.content || '',
                    workspaceId: ws.id,
                    folderId: doc.folder_id,
                    createdAt: new Date(doc.created_at),
                    updatedAt: new Date(doc.updated_at)
                }));
                // Transform forms from Supabase
                const forms = formsData.filter((f) => f.workspace_id === ws.id).map((f) => ({
                    id: f.id,
                    name: f.name,
                    description: f.description || '',
                    workspaceId: ws.id,
                    folderId: f.folder_id,
                    linkedBoardId: f.linked_board_id,
                    submissionCount: f.submission_count || 0,
                    isActive: f.is_active || true,
                    createdAt: new Date(f.created_at),
                    updatedAt: new Date(f.updated_at)
                }));
                // Transform apps from Supabase
                const apps = appsData.filter((a) => a.workspace_id === ws.id).map((a) => ({
                    id: a.id,
                    appId: a.app_id,
                    name: a.name,
                    description: a.description || '',
                    workspaceId: ws.id,
                    folderId: a.folder_id,
                    isActive: a.is_active || true,
                    createdAt: new Date(a.created_at),
                    updatedAt: new Date(a.updated_at)
                }));
                // Organize items into folders based on their folderId
                // Debug: Check board-to-folder matching
                const boardsWithFolderId = boards.filter(b => b.folderId);
                const boardsWithoutFolderId = boards.filter(b => !b.folderId);
                console.log(`📊 Board distribution: ${boards.length} total, ${boardsWithFolderId.length} with folder_id, ${boardsWithoutFolderId.length} without folder_id`);
                if (boardsWithFolderId.length > 0) {
                    console.log(`  Board folder_ids:`, boardsWithFolderId.map(b => ({ name: b.name, folderId: b.folderId })));
                }
                allFolders.forEach(folder => {
                    folder.boards = boards.filter(board => board.folderId === folder.id);
                    folder.dashboards = dashboards.filter(dashboard => dashboard.folderId === folder.id);
                    folder.docs = docs.filter(doc => doc.folderId === folder.id);
                    folder.forms = forms.filter(form => form.folderId === folder.id);
                    folder.apps = apps.filter(app => app.folderId === folder.id);
                    // Debug: Log folder content
                    if (folder.boards.length > 0 || folder.dashboards.length > 0 || folder.docs.length > 0) {
                        console.log(`  ✅ Folder "${folder.name}" (id: ${folder.id}): ${folder.boards.length} boards, ${folder.dashboards.length} dashboards, ${folder.docs.length} docs, ${folder.forms.length} forms, ${folder.apps.length} apps`);
                    }
                });
                // Build folder hierarchy - organize folders with parentId into their parent's subFolders
                const rootFolders = [];
                const foldersById = new Map(allFolders.map(f => [f.id, f]));
                allFolders.forEach(folder => {
                    if (folder.parentId) {
                        // This folder has a parent - add it to parent's subFolders
                        const parent = foldersById.get(folder.parentId);
                        if (parent) {
                            parent.subFolders.push(folder);
                        }
                        else {
                            // Parent not found, add to root as fallback
                            rootFolders.push(folder);
                        }
                    }
                    else {
                        // No parent - this is a root-level folder
                        rootFolders.push(folder);
                    }
                });
                // Only return items without folderId at workspace root level
                const rootBoards = boards.filter(board => !board.folderId);
                const rootDashboards = dashboards.filter(dashboard => !dashboard.folderId);
                const rootDocs = docs.filter(doc => !doc.folderId);
                const rootForms = forms.filter(form => !form.folderId);
                const rootApps = apps.filter(app => !app.folderId);
                // Debug: Check for unmatched folder IDs
                const matchedFolderIds = new Set(boards.filter(b => b.folderId).map(b => b.folderId));
                const existingFolderIds = new Set(allFolders.map(f => f.id));
                const unmatchedIds = Array.from(matchedFolderIds).filter(id => !existingFolderIds.has(id));
                if (unmatchedIds.length > 0) {
                    console.warn(`⚠️ Unmatched folder IDs: ${unmatchedIds.join(', ')} (boards reference folders that don't exist)`);
                }
                // Count boards by folder
                const boardsByFolder = new Map();
                boards.forEach(board => {
                    if (board.folderId) {
                        boardsByFolder.set(board.folderId, (boardsByFolder.get(board.folderId) || 0) + 1);
                    }
                });
                console.log(`✅ Workspace ${ws.name}: ${allFolders.length} total folders (${rootFolders.length} root), ${rootBoards.length} root boards (${boards.length} total), ${rootDashboards.length} root dashboards, ${rootDocs.length} root docs, ${rootForms.length} root forms, ${rootApps.length} root apps`);
                if (boardsByFolder.size > 0) {
                    console.log(`   Boards in folders:`, Object.fromEntries(boardsByFolder));
                }
                return {
                    id: ws.id,
                    name: ws.name,
                    description: ws.description || '',
                    type: 'open',
                    color: '#579bfc',
                    icon: ws.name.includes('🐮') ? '🐮' : '🏢',
                    isDefault: true,
                    ownerId: ws.created_by,
                    memberIds: [ws.created_by],
                    createdAt: new Date(ws.created_at),
                    updatedAt: new Date(ws.updated_at),
                    folders: rootFolders,
                    boards: rootBoards,
                    dashboards: rootDashboards,
                    docs: rootDocs,
                    forms: rootForms,
                    apps: rootApps
                };
            });
            console.log('✅ Transformed workspaces:', workspaces);
            return workspaces;
        }
        catch (error) {
            console.error('Failed to load workspaces from Supabase:', error);
            return [];
        }
    }
    /**
     * Load tasks for a specific board
     */
    async loadBoardTasks(boardId) {
        try {
            console.log(`🔍 Loading tasks for board ${boardId}...`);
            const { data: tasksData, error } = await supabase
                .from('board_items')
                .select(`
          *,
          board_groups (
            id,
            title,
            color,
            position
          )
        `)
                .eq('board_id', boardId)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading tasks:', error);
                return [];
            }
            console.log('✅ Loaded tasks:', tasksData);
            return tasksData;
        }
        catch (error) {
            console.error('Failed to load tasks:', error);
            return [];
        }
    }
    /**
     * Get color for board based on its title/type
     */
    getBoardColor(title) {
        const colorMap = {
            'FIN': '#ff6b35', // Orange for Finance
            'TECH': '#00c875', // Green for Technology
            'SALE': '#9d34da', // Purple for Sales
            'STR': '#579bfc', // Blue for Strategy
            'LAUNCH': '#ff9f40', // Yellow for Launch
            'Q1': '#e2445c', // Red for Strategic missions
        };
        // Find matching color based on title content
        for (const [key, color] of Object.entries(colorMap)) {
            if (title.toUpperCase().includes(key)) {
                return color;
            }
        }
        return '#579bfc'; // Default blue
    }
    /**
     * Test Supabase connection
     */
    async testConnection() {
        try {
            console.log('🔍 Testing Supabase connection...');
            const { data, error } = await supabase
                .from('workspaces')
                .select('id')
                .limit(1);
            if (error) {
                console.error('Supabase connection test failed:', error);
                return false;
            }
            console.log('✅ Supabase connection successful');
            return true;
        }
        catch (error) {
            console.error('Supabase connection test error:', error);
            return false;
        }
    }
    // ==================== FOLDER CRUD ====================
    async createFolder(data) {
        try {
            console.log('📂 Creating folder in Supabase:', data);
            const { data: folder, error } = await supabase
                .from('folders')
                .insert({
                workspace_id: data.workspaceId,
                parent_folder_id: data.parentId || null,
                name: data.name,
                description: data.description || '',
                color: data.color || '#579bfc',
                created_by: data.createdBy
            })
                .select()
                .single();
            if (error) {
                console.error('❌ Error creating folder:', error);
                return null;
            }
            console.log('✅ Folder created:', folder);
            return folder;
        }
        catch (error) {
            console.error('❌ Failed to create folder:', error);
            return null;
        }
    }
    async loadFolders(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('folders')
                .select('*')
                .eq('workspace_id', workspaceId)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading folders:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load folders:', error);
            return [];
        }
    }
    async updateFolder(folderId, updates) {
        try {
            const { data, error } = await supabase
                .from('folders')
                .update(updates)
                .eq('id', folderId)
                .select()
                .single();
            if (error) {
                console.error('Error updating folder:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to update folder:', error);
            return null;
        }
    }
    async deleteFolder(folderId) {
        try {
            const { error } = await supabase
                .from('folders')
                .delete()
                .eq('id', folderId);
            if (error) {
                console.error('Error deleting folder:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete folder:', error);
            return false;
        }
    }
    // ==================== DASHBOARD CRUD ====================
    async createDashboard(data) {
        try {
            console.log('📊 Creating dashboard in Supabase:', data);
            const { data: dashboard, error } = await supabase
                .from('dashboards')
                .insert({
                workspace_id: data.workspaceId,
                folder_id: data.folderId || null,
                name: data.name,
                description: data.description || '',
                created_by: data.createdBy
            })
                .select()
                .single();
            if (error) {
                console.error('❌ Error creating dashboard:', error);
                return null;
            }
            console.log('✅ Dashboard created:', dashboard);
            return dashboard;
        }
        catch (error) {
            console.error('❌ Failed to create dashboard:', error);
            return null;
        }
    }
    async loadDashboards(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('dashboards')
                .select('*')
                .eq('workspace_id', workspaceId)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading dashboards:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load dashboards:', error);
            return [];
        }
    }
    // ==================== DOC CRUD ====================
    async createDoc(data) {
        try {
            console.log('📝 Creating doc in Supabase:', data);
            const { data: doc, error } = await supabase
                .from('docs')
                .insert({
                workspace_id: data.workspaceId,
                folder_id: data.folderId || null,
                name: data.name,
                content: data.content || '',
                created_by: data.createdBy
            })
                .select()
                .single();
            if (error) {
                console.error('❌ Error creating doc:', error);
                return null;
            }
            console.log('✅ Doc created:', doc);
            return doc;
        }
        catch (error) {
            console.error('❌ Failed to create doc:', error);
            return null;
        }
    }
    async loadDocs(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('docs')
                .select('*')
                .eq('workspace_id', workspaceId)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading docs:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load docs:', error);
            return [];
        }
    }
    async deleteDoc(docId) {
        try {
            const { error } = await supabase
                .from('docs')
                .delete()
                .eq('id', docId);
            if (error) {
                console.error('Error deleting doc:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete doc:', error);
            return false;
        }
    }
    //==================== DASHBOARD CRUD ====================
    async deleteDashboard(dashboardId) {
        try {
            const { error } = await supabase
                .from('dashboards')
                .delete()
                .eq('id', dashboardId);
            if (error) {
                console.error('Error deleting dashboard:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete dashboard:', error);
            return false;
        }
    }
    // ==================== FORM CRUD ====================
    async createForm(data) {
        try {
            console.log('📋 Creating form in Supabase:', data);
            const { data: form, error } = await supabase
                .from('forms')
                .insert({
                workspace_id: data.workspaceId,
                folder_id: data.folderId || null,
                linked_board_id: data.linkedBoardId || null,
                name: data.name,
                description: data.description || '',
                created_by: data.createdBy
            })
                .select()
                .single();
            if (error) {
                console.error('❌ Error creating form:', error);
                return null;
            }
            console.log('✅ Form created:', form);
            return form;
        }
        catch (error) {
            console.error('❌ Failed to create form:', error);
            return null;
        }
    }
    async loadForms(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('forms')
                .select('*')
                .eq('workspace_id', workspaceId)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading forms:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load forms:', error);
            return [];
        }
    }
    async deleteForm(formId) {
        try {
            const { error } = await supabase
                .from('forms')
                .delete()
                .eq('id', formId);
            if (error) {
                console.error('Error deleting form:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete form:', error);
            return false;
        }
    }
    // ==================== WORKSPACE APP CRUD ====================
    async createWorkspaceApp(data) {
        try {
            console.log('🧩 Creating workspace app in Supabase:', data);
            const { data: app, error } = await supabase
                .from('workspace_apps')
                .insert({
                workspace_id: data.workspaceId,
                folder_id: data.folderId || null,
                app_id: data.appId,
                name: data.name,
                description: data.description || '',
                created_by: data.createdBy
            })
                .select()
                .single();
            if (error) {
                console.error('❌ Error creating workspace app:', error);
                return null;
            }
            console.log('✅ Workspace app created:', app);
            return app;
        }
        catch (error) {
            console.error('❌ Failed to create workspace app:', error);
            return null;
        }
    }
    async loadWorkspaceApps(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('workspace_apps')
                .select('*')
                .eq('workspace_id', workspaceId)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading workspace apps:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load workspace apps:', error);
            return [];
        }
    }
    async deleteApp(appId) {
        try {
            const { error } = await supabase
                .from('workspace_apps')
                .delete()
                .eq('id', appId);
            if (error) {
                console.error('Error deleting workspace app:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete workspace app:', error);
            return false;
        }
    }
    // ==================== BULK LOAD METHODS FOR MULTIPLE WORKSPACES ====================
    /**
     * Load folders for multiple workspaces at once
     */
    async loadAllFolders(workspaceIds) {
        if (workspaceIds.length === 0)
            return [];
        try {
            const { data, error } = await supabase
                .from('folders')
                .select('*')
                .in('workspace_id', workspaceIds)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading folders for all workspaces:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load folders for all workspaces:', error);
            return [];
        }
    }
    /**
     * Load dashboards for multiple workspaces at once
     */
    async loadAllDashboards(workspaceIds) {
        if (workspaceIds.length === 0)
            return [];
        try {
            const { data, error } = await supabase
                .from('dashboards')
                .select('*')
                .in('workspace_id', workspaceIds)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading dashboards for all workspaces:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load dashboards for all workspaces:', error);
            return [];
        }
    }
    /**
     * Load docs for multiple workspaces at once
     */
    async loadAllDocs(workspaceIds) {
        if (workspaceIds.length === 0)
            return [];
        try {
            const { data, error } = await supabase
                .from('docs')
                .select('*')
                .in('workspace_id', workspaceIds)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading docs for all workspaces:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load docs for all workspaces:', error);
            return [];
        }
    }
    /**
     * Load forms for multiple workspaces at once
     */
    async loadAllForms(workspaceIds) {
        if (workspaceIds.length === 0)
            return [];
        try {
            const { data, error } = await supabase
                .from('forms')
                .select('*')
                .in('workspace_id', workspaceIds)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading forms for all workspaces:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load forms for all workspaces:', error);
            return [];
        }
    }
    /**
     * Load workspace apps for multiple workspaces at once
     */
    async loadAllWorkspaceApps(workspaceIds) {
        if (workspaceIds.length === 0)
            return [];
        try {
            const { data, error } = await supabase
                .from('workspace_apps')
                .select('*')
                .in('workspace_id', workspaceIds)
                .order('created_at', { ascending: true });
            if (error) {
                console.error('Error loading workspace apps for all workspaces:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to load workspace apps for all workspaces:', error);
            return [];
        }
    }
    // ==================== STAR/FAVORITE TOGGLE METHODS ====================
    /**
     * Toggle doc star status
     */
    async toggleDocStar(docId) {
        try {
            // Get current doc to check starred status
            const { data: doc, error: fetchError } = await supabase
                .from('docs')
                .select('is_starred')
                .eq('id', docId)
                .single();
            if (fetchError || !doc) {
                console.error('Error fetching doc:', fetchError);
                return false;
            }
            // Toggle the starred status
            const { error } = await supabase
                .from('docs')
                .update({ is_starred: !doc.is_starred })
                .eq('id', docId);
            if (error) {
                console.error('Error toggling doc star:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to toggle doc star:', error);
            return false;
        }
    }
    /**
     * Toggle dashboard star status
     */
    async toggleDashboardStar(dashboardId) {
        try {
            // Get current dashboard to check starred status
            const { data: dashboard, error: fetchError } = await supabase
                .from('dashboards')
                .select('is_starred')
                .eq('id', dashboardId)
                .single();
            if (fetchError || !dashboard) {
                console.error('Error fetching dashboard:', fetchError);
                return false;
            }
            // Toggle the starred status
            const { error } = await supabase
                .from('dashboards')
                .update({ is_starred: !dashboard.is_starred })
                .eq('id', dashboardId);
            if (error) {
                console.error('Error toggling dashboard star:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to toggle dashboard star:', error);
            return false;
        }
    }
    /**
     * Toggle form star status
     */
    async toggleFormStar(formId) {
        try {
            // Get current form to check starred status
            const { data: form, error: fetchError } = await supabase
                .from('forms')
                .select('is_starred')
                .eq('id', formId)
                .single();
            if (fetchError || !form) {
                console.error('Error fetching form:', fetchError);
                return false;
            }
            // Toggle the starred status
            const { error } = await supabase
                .from('forms')
                .update({ is_starred: !form.is_starred })
                .eq('id', formId);
            if (error) {
                console.error('Error toggling form star:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to toggle form star:', error);
            return false;
        }
    }
    /**
     * Toggle workspace app star status
     */
    async toggleAppStar(appId) {
        try {
            // Get current app to check starred status
            const { data: app, error: fetchError } = await supabase
                .from('workspace_apps')
                .select('is_starred')
                .eq('id', appId)
                .single();
            if (fetchError || !app) {
                console.error('Error fetching workspace app:', fetchError);
                return false;
            }
            // Toggle the starred status
            const { error } = await supabase
                .from('workspace_apps')
                .update({ is_starred: !app.is_starred })
                .eq('id', appId);
            if (error) {
                console.error('Error toggling workspace app star:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to toggle workspace app star:', error);
            return false;
        }
    }
    // =====================================================
    // WORKSPACE APPS METHODS
    // =====================================================
    async getWorkspaceApps(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('workspace_apps')
                .select('app_id')
                .eq('workspace_id', workspaceId);
            if (error) {
                console.error('Error fetching workspace apps:', error);
                return [];
            }
            return (data || []).map(row => row.app_id);
        }
        catch (error) {
            console.error('Failed to fetch workspace apps:', error);
            return [];
        }
    }
    async addWorkspaceApp(workspaceId, appId) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { error } = await supabase
                .from('workspace_apps')
                .insert({
                workspace_id: workspaceId,
                app_id: appId,
                added_by: user.id
            });
            if (error) {
                // If error is duplicate, ignore it (already added)
                if (error.code === '23505') {
                    return true;
                }
                console.error('Error adding workspace app:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to add workspace app:', error);
            return false;
        }
    }
    async removeWorkspaceApp(workspaceId, appId) {
        try {
            const { error } = await supabase
                .from('workspace_apps')
                .delete()
                .eq('workspace_id', workspaceId)
                .eq('app_id', appId);
            if (error) {
                console.error('Error removing workspace app:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to remove workspace app:', error);
            return false;
        }
    }
    async updateWorkspaceAppOrder(workspaceId, appId, newSortOrder) {
        // Note: sort_order column doesn't exist in workspace_apps table
        // App ordering is handled at the UI level
        console.log('updateWorkspaceAppOrder: sort_order not implemented in database');
        return true;
    }
}
export const supabaseWorkspaceService = new SupabaseWorkspaceService();
